<template>
    <div class="quill-editor">
        <div class="quill-editor_label" v-if="label">
            {{ label }}
        </div>
        <div
            ref="quillContainer"
            class="quill-editor_container"
            :class="{ 'quill-editor_container--error': hasError }"
        ></div>
        <div class="quill-editor_error" v-if="hasError">
            {{ errorMessage }}
        </div>

    </div>
</template>

<script setup>
    import { ref, computed, onMounted, watch, nextTick } from 'vue';
    import Quill from 'quill';
    import 'quill/dist/quill.snow.css';

    const props = defineProps({
        modelValue: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Digite sua mensagem...'
        },
        readonly: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ''
        },
        height: {
            type: String,
            default: '200px'
        },
        maxHeight: {
            type: String,
            default: '400px'
        }
    });

    const emit = defineEmits(['update:modelValue']);

    const quillContainer = ref(null);
    let quillInstance = null;

    const hasError = computed(() => !!props.error);
    const errorMessage = computed(() => props.error);

    // Configuração simples do Quill
    const quillOptions = {
        theme: 'snow',
        placeholder: props.placeholder,
        readOnly: props.readonly,
        modules: {
            toolbar: [
                ['bold', 'italic', 'underline'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                ['link']
            ]
        }
    };

    const initializeQuill = () => {
        if (quillContainer.value && !quillInstance) {
            quillInstance = new Quill(quillContainer.value, quillOptions);

            // Set initial content
            if (props.modelValue) {
                quillInstance.root.innerHTML = props.modelValue;
            }

            // Listen for text changes
            quillInstance.on('text-change', () => {
                const html = quillInstance.root.innerHTML;
                emit('update:modelValue', html);
            });

            // Set height and max-height
            quillInstance.root.style.minHeight = props.height;
            quillInstance.root.style.maxHeight = props.maxHeight;
            quillInstance.root.style.overflowY = 'auto';
        }
    };

    // Watch for external changes
    watch(() => props.modelValue, (newValue) => {
        if (quillInstance && newValue !== quillInstance.root.innerHTML) {
            quillInstance.root.innerHTML = newValue || '';
        }
    });

    // Watch for readonly changes
    watch(() => props.readonly, (newValue) => {
        if (quillInstance) {
            quillInstance.enable(!newValue);
        }
    });

    onMounted(() => {
        nextTick(() => {
            initializeQuill();
        });
    });
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .quill-editor {
        width: 100%;
    }

    .quill-editor_label {
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary);
        margin-bottom: 0.5em;
        display: block;
    }

    .quill-editor_container {
        border-radius: 0;
        overflow: hidden;
        border: 1px solid rgba(0, 0, 0, 0.08);
        background: white;
        box-shadow:
            0 1px 3px rgba(0, 0, 0, 0.12),
            0 1px 2px rgba(0, 0, 0, 0.24);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
            box-shadow:
                0 3px 12px rgba(0, 0, 0, 0.15),
                0 1px 4px rgba(0, 0, 0, 0.3);
            transform: translateY(-1px);
        }

        &:focus-within {
            border-color: rgba(59, 130, 246, 0.3);
            box-shadow:
                0 8px 25px rgba(0, 0, 0, 0.15),
                0 0 0 3px rgba(59, 130, 246, 0.1);
            transform: translateY(-2px);
        }

        &--error {
            border-color: rgba(239, 68, 68, 0.3);
            box-shadow:
                0 1px 3px rgba(239, 68, 68, 0.2),
                0 1px 2px rgba(0, 0, 0, 0.24);

            &:focus-within {
                box-shadow:
                    0 8px 25px rgba(239, 68, 68, 0.2),
                    0 0 0 3px rgba(239, 68, 68, 0.1);
                transform: translateY(-2px);
            }
        }
    }

    .quill-editor_error {
        font-size: 0.75em;
        color: var(--colorRed);
        margin-top: 0.5em;
        display: block;
    }

    // Customização do tema Snow do Quill
    :deep(.ql-toolbar) {
        border: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        padding: 1em 1.25em;
        backdrop-filter: blur(10px);
    }

    :deep(.ql-container) {
        border: none;
        font-family: inherit;
        font-size: 0.9em;
        background: white;
        border-top: 1px solid rgba(0, 0, 0, 0.06);
    }

    :deep(.ql-editor) {
        padding: 1.25em;
        min-height: 200px;
        max-height: 400px;
        overflow-y: auto;
        color: var(--textPrimary);
        line-height: 1.6;
        border: none;
        outline: none;
        background: transparent;

        &:focus {
            outline: none;
            box-shadow: none;
        }

        &.ql-blank::before {
            color: var(--textSecondary);
            font-style: normal;
            opacity: 0.6;
            font-weight: 400;
        }

        p {
            margin-bottom: 0.75em;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    :deep(.ql-toolbar .ql-stroke) {
        stroke: #64748b;
        transition: stroke 0.2s ease;
    }

    :deep(.ql-toolbar .ql-fill) {
        fill: #64748b;
        transition: fill 0.2s ease;
    }

    :deep(.ql-toolbar .ql-picker-label) {
        color: #64748b;
        transition: color 0.2s ease;
    }

    :deep(.ql-toolbar button) {
        width: 2.25em;
        height: 2.25em;
        border-radius: 0.5em;
        margin: 0 0.125em;
        transition: all 0.2s ease;
        border: 1px solid transparent;

        &:hover {
            background: rgba(59, 130, 246, 0.1);
            border-color: rgba(59, 130, 246, 0.2);
            transform: translateY(-1px);

            .ql-stroke {
                stroke: #3b82f6;
            }

            .ql-fill {
                fill: #3b82f6;
            }
        }

        &.ql-active {
            background: linear-gradient(135deg, #3b82f6, #2563eb);
            border-color: #2563eb;
            box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

            .ql-stroke {
                stroke: white;
            }

            .ql-fill {
                fill: white;
            }
        }
    }

    :deep(.ql-toolbar .ql-picker) {
        color: #64748b;

        &:hover {
            color: #3b82f6;
        }
    }

    @include screen_medias(s) {
        .quill-editor_container {
            border-radius: 0;

            &:hover {
                transform: none;
            }

            &:focus-within {
                transform: none;
            }
        }

        :deep(.ql-toolbar) {
            padding: 0.75em 1em;

            button {
                width: 2em;
                height: 2em;
                margin: 0 0.0625em;

                &:hover {
                    transform: none;
                }
            }
        }

        :deep(.ql-editor) {
            padding: 1em;
            min-height: 150px;
            max-height: 300px;
        }
    }

</style>